import { useState, type FormEvent } from 'react';
import { Alert, Button, Card, Field, Input } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatDate } from './Credentials';

export function Profile() {
  usePageTitle('Profile — WaveQA');

  const { user, signOut } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await api.changePassword({
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      setDone(true);
      // Changing a password revokes every session, including this one. Signing
      // out locally keeps the UI honest instead of leaving the user looking
      // signed in against a server that has already dropped them.
      window.setTimeout(() => void signOut(), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('Something went wrong.', 0));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-white">Profile</h2>

      <Card className="mt-5">
        <h3 className="text-base font-semibold text-white">Account</h3>
        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Email" value={user?.email ?? '—'} />
          <Row label="Name" value={user?.display_name || 'Not set'} />
          <Row label="Status" value={user?.status ?? '—'} />
          <Row
            label="Email verified"
            value={user?.email_verified_at ? formatDate(user.email_verified_at) : 'No'}
          />
          <Row label="Time zone" value={user?.time_zone ?? '—'} />
          <Row
            label="Member since"
            value={user?.created_at ? formatDate(user.created_at) : '—'}
          />
        </dl>
      </Card>

      <Card className="mt-4">
        <h3 className="text-base font-semibold text-white">Change password</h3>
        <p className="mt-1.5 text-sm text-hull-400">
          Changing your password signs out every device, including this one.
        </p>

        <form className="mt-5 max-w-sm" onSubmit={handleSubmit} noValidate>
          {error && <Alert tone="error">{error.message}</Alert>}
          {done && (
            <Alert tone="success">
              Password changed. Signing you out — sign in again with the new one.
            </Alert>
          )}

          {!done && (
            <>
              <Field
                label="Current password"
                htmlFor="current"
                errors={error?.fields?.current_password}
              >
                <Input
                  id="current"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </Field>

              <Field
                label="New password"
                htmlFor="next"
                hint="At least 12 characters, with upper and lower case, a digit and a symbol."
                errors={error?.fields?.new_password}
              >
                <Input
                  id="next"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                />
              </Field>

              <Field
                label="Confirm new password"
                htmlFor="confirm-new"
                errors={error?.fields?.confirm_password}
              >
                <Input
                  id="confirm-new"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </Field>

              <Button type="submit" disabled={busy}>
                {busy ? 'Changing…' : 'Change password'}
              </Button>
            </>
          )}
        </form>
      </Card>

      <Card className="mt-4">
        <h3 className="text-base font-semibold text-white">Security</h3>
        <ul className="mt-3 space-y-2 text-sm text-hull-300">
          <li>Sign-in requires a one-time code emailed to you, every time.</li>
          <li>Your password is stored as an Argon2id hash and cannot be recovered.</li>
          <li>Agent secrets are hashed too — they are shown once and never again.</li>
          <li>
            Every sign-in, credential change and QA run is recorded in an
            append-only audit trail.
          </li>
        </ul>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-hull-800 py-1.5">
      <dt className="text-hull-400">{label}</dt>
      <dd className="truncate text-hull-200">{value}</dd>
    </div>
  );
}
